/**
 * What a day draws, before it is drawn.
 *
 * On a 393px phone two empty add rows cost 112px and offer nothing but two
 * taps. Merged into one split row they cost 44px and offer the same two taps,
 * which is −68px per day and −476px per week — about 16% of the scroll.
 *
 * The merge is **presentational only**. Each half resolves to its own existing
 * `slotKey`: `slotKey = mi * 7 + di` with `mi ∈ {0,1,2}` is load-bearing across
 * mobile, desktop, the config screen, the generator, leftovers and
 * `DEFAULT_SELECTION`, and `slotKey(0, 7) === slotKey(1, 0)`. No fourth meal
 * index, no pseudo-slot — this module never invents a slot, it only decides
 * which two existing ones share a row.
 */
import { MEALS, slotKey } from '../constants';

export type MealDef = (typeof MEALS)[number];

export type MealUnit =
  | { kind: 'meal'; meal: MealDef }
  | { kind: 'split'; first: MealDef; second: MealDef };

/** The slot a meal occupies on a given day. */
export function slotOf(meal: MealDef, di: number): number {
  return slotKey(MEALS.indexOf(meal), di);
}

/**
 * Group a day's meals into rows.
 *
 * Only déjeuner and dîner merge, only when adjacent, and only when **both** are
 * empty — a filled meal keeps its own card, and an entry (resto, restes, note)
 * or an unsettled proposal is not empty. Every other day shape is untouched.
 */
export function buildMealUnits(
  meals: readonly MealDef[],
  di: number,
  isEmptySlot: (sk: number) => boolean,
): MealUnit[] {
  const units: MealUnit[] = [];
  for (let i = 0; i < meals.length; i++) {
    const meal = meals[i];
    const next = meals[i + 1];
    const merges =
      meal.mealType === 'breakfast'
      && next?.mealType === 'lunch'
      && isEmptySlot(slotOf(meal, di))
      && isEmptySlot(slotOf(next, di));
    if (merges) {
      units.push({ kind: 'split', first: meal, second: next });
      i += 1;
      continue;
    }
    units.push({ kind: 'meal', meal });
  }
  return units;
}
